import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Bitcoin, Building, ArrowRight, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { transferAPI, bankAPI, walletAPI } from '../services/api';
import { CRYPTO_SYMBOLS, CRYPTO_NAMES, formatCurrency } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Per-category coin definitions with fixed network info
const SEND_COINS = {
  stablecoin: [
    {
      id: 'USDT', name: 'Tether USD', networkTag: 'TRC20', networkFull: 'Tron Network',
      placeholder: 'TRX address (starts with T...)',
      warning: 'Tron (TRC20) network only — do NOT send via ERC20 or BEP20',
    },
    {
      id: 'USDC', name: 'USD Coin', networkTag: 'BASE', networkFull: 'Base Network',
      placeholder: '0x... (Base network address)',
      warning: 'Base network only — do NOT send via Ethereum (ERC20)',
    },
  ],
  crypto: [
    {
      id: 'BTC', name: 'Bitcoin', networkTag: 'BTC', networkFull: 'Bitcoin Network',
      placeholder: 'bc1... or 1... or 3...',
      warning: 'Bitcoin (BTC) network only',
    },
    {
      id: 'ETH', name: 'Ethereum', networkTag: 'ERC20', networkFull: 'Ethereum',
      placeholder: '0x... (Ethereum address)',
      warning: 'Ethereum (ERC20) network only',
    },
    {
      id: 'SOL', name: 'Solana', networkTag: 'SOL', networkFull: 'Solana',
      placeholder: 'Solana wallet address',
      warning: 'Solana (SPL) network only',
    },
    {
      id: 'TON', name: 'Toncoin', networkTag: 'TON', networkFull: 'TON Network',
      placeholder: 'EQ... or UQ... TON address',
      warning: 'TON network only',
    },
  ],
};

const CATEGORIES = [
  {
    id: 'stablecoin',
    Icon: DollarSign,
    title: 'Stablecoin',
    subtitle: 'Instant',
    tags: ['USDT · TRC20', 'USDC · BASE'],
    color: '#26A17B',
    description: 'Send USDT or USDC',
  },
  {
    id: 'crypto',
    Icon: Bitcoin,
    title: 'Crypto',
    subtitle: '2-5 min',
    tags: ['BTC', 'ETH', 'SOL', 'TON'],
    color: '#F7931A',
    description: 'Bitcoin, Ethereum & more',
  },
  {
    id: 'bank',
    Icon: Building,
    title: 'UAE Bank',
    subtitle: '< 24 hrs',
    tags: ['AED only'],
    color: '#00F0FF',
    description: 'Withdraw to UAE bank',
  },
];

const Send = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [balances, setBalances] = useState([]);
  const [aedBalance, setAedBalance] = useState(0);
  const [uaeBanks, setUaeBanks] = useState([]);

  // Crypto / stablecoin send form
  const [cryptoForm, setCryptoForm] = useState({
    crypto: '', amount: '', to_address: '', network: '',
  });
  const [sending, setSending] = useState(false);

  // Bank transfer form
  const [bankForm, setBankForm] = useState({
    amount: '', bank_name: '', account_number: '', iban: '',
    account_holder_name: '', swift_code: '',
  });
  const [bankErrors, setBankErrors] = useState({});
  const [bankSending, setBankSending] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balRes, banksRes] = await Promise.all([
        walletAPI.getBalances(),
        bankAPI.getUAEBanks().catch(() => ({ data: { banks: [] } })),
      ]);
      const all = balRes.data;
      setBalances(all.filter(b => b.crypto !== 'AED'));
      setAedBalance(all.find(b => b.crypto === 'AED')?.balance || 0);
      setUaeBanks(banksRes.data.banks || []);
    } catch {
      toast.error('Failed to load balances');
    }
  };

  const handleCategorySelect = (catId) => {
    setSelectedCategory(catId);
    if (catId === 'stablecoin') {
      const coin = SEND_COINS.stablecoin[0];
      setCryptoForm({ crypto: coin.id, amount: '', to_address: '', network: coin.networkFull });
    } else if (catId === 'crypto') {
      const coin = SEND_COINS.crypto[0];
      setCryptoForm({ crypto: coin.id, amount: '', to_address: '', network: coin.networkFull });
    }
  };

  const getSelectedCoin = () => {
    if (!selectedCategory || selectedCategory === 'bank') return null;
    const coins = SEND_COINS[selectedCategory] || [];
    return coins.find(c => c.id === cryptoForm.crypto) || coins[0];
  };

  const handleCryptoSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const coin = getSelectedCoin();
      await transferAPI.transferCrypto({
        crypto: cryptoForm.crypto,
        amount: cryptoForm.amount,
        to_address: cryptoForm.to_address,
        network: coin?.networkFull || cryptoForm.network,
      });
      toast.success(`Sent ${cryptoForm.amount} ${cryptoForm.crypto} successfully!`);
      setCryptoForm(prev => ({ ...prev, amount: '', to_address: '' }));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSending(false);
    }
  };

  // ── Bank validation ────────────────────────────────────────────────────────
  const validateIBAN = (iban) => {
    if (!iban) return { valid: false, message: 'IBAN is required' };
    iban = iban.replace(/\s/g, '').toUpperCase();
    if (iban.length !== 23) return { valid: false, message: 'UAE IBAN must be 23 characters' };
    if (!iban.startsWith('AE')) return { valid: false, message: 'UAE IBAN must start with "AE"' };
    if (!/^AE\d{21}$/.test(iban)) return { valid: false, message: 'Invalid IBAN format' };
    return { valid: true, message: '' };
  };

  const validateAccountNumber = (acc) => {
    if (!acc) return { valid: false, message: 'Account number is required' };
    acc = acc.replace(/\s/g, '');
    if (!/^\d+$/.test(acc)) return { valid: false, message: 'Digits only' };
    if (acc.length < 10 || acc.length > 16) return { valid: false, message: 'Must be 10-16 digits' };
    return { valid: true, message: '' };
  };

  const validateBankAmount = (amount) => {
    const num = parseFloat(amount);
    if (!amount || isNaN(num)) return { valid: false, message: 'Amount required' };
    if (num < 10) return { valid: false, message: 'Minimum 10 AED' };
    if (num > 100000) return { valid: false, message: 'Maximum 100,000 AED' };
    if (num > aedBalance) return { valid: false, message: 'Insufficient balance' };
    return { valid: true, message: '' };
  };

  const validateHolderName = (name) => {
    if (!name || name.trim().length < 2) return { valid: false, message: 'Name required (min 2 chars)' };
    return { valid: true, message: '' };
  };

  const handleBankInput = (field, value) => {
    setBankForm(prev => ({ ...prev, [field]: value }));
    let validation;
    switch (field) {
      case 'iban':               validation = validateIBAN(value);         break;
      case 'account_number':     validation = validateAccountNumber(value); break;
      case 'amount':             validation = validateBankAmount(value);    break;
      case 'account_holder_name': validation = validateHolderName(value);  break;
      default:                   validation = { valid: true, message: '' };
    }
    setBankErrors(prev => ({ ...prev, [field]: validation.valid ? '' : validation.message }));
  };

  const formatIBAN = (value) => {
    const clean = value.replace(/\s/g, '').toUpperCase();
    return clean.match(/.{1,4}/g)?.join(' ') || clean;
  };

  const isBankFormValid = () => (
    bankForm.amount && bankForm.bank_name && bankForm.account_number &&
    bankForm.iban && bankForm.account_holder_name &&
    !bankErrors.iban && !bankErrors.account_number &&
    !bankErrors.amount && !bankErrors.account_holder_name
  );

  const handleBankSend = async (e) => {
    e.preventDefault();
    if (!bankForm.bank_name) { toast.error('Please select a bank'); return; }
    const ibanV   = validateIBAN(bankForm.iban);
    const accV    = validateAccountNumber(bankForm.account_number);
    const amtV    = validateBankAmount(bankForm.amount);
    const holderV = validateHolderName(bankForm.account_holder_name);
    if (!ibanV.valid || !accV.valid || !amtV.valid || !holderV.valid) {
      setBankErrors({
        iban: ibanV.message, account_number: accV.message,
        amount: amtV.message, account_holder_name: holderV.message,
      });
      toast.error('Please fix validation errors');
      return;
    }
    setBankSending(true);
    try {
      await bankAPI.transfer({
        ...bankForm,
        iban: bankForm.iban.replace(/\s/g, '').toUpperCase(),
        account_number: bankForm.account_number.replace(/\s/g, ''),
        amount: parseFloat(bankForm.amount),
      });
      toast.success(`Transfer of ${formatCurrency(parseFloat(bankForm.amount))} initiated!`);
      setBankForm({ amount: '', bank_name: '', account_number: '', iban: '', account_holder_name: '', swift_code: '' });
      setBankErrors({});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setBankSending(false);
    }
  };

  const selectedCoin   = getSelectedCoin();
  const selectedBalance = selectedCoin ? balances.find(b => b.crypto === selectedCoin.id) : null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Header */}
          <div className="flex items-center gap-3 mb-2">
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className="text-4xl font-primary font-bold text-white">SEND</h1>
          </div>
          <p className="text-gray-400 font-secondary mb-8">
            {!selectedCategory
              ? 'Choose how you want to send'
              : selectedCategory === 'stablecoin'
              ? 'Send stablecoins instantly'
              : selectedCategory === 'crypto'
              ? 'Send crypto to any wallet'
              : 'Withdraw AED to your UAE bank account'}
          </p>

          {/* ── Category Cards ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {CATEGORIES.map(({ id, Icon, title, subtitle, tags, color }) => {
              const isSelected = selectedCategory === id;
              return (
                <motion.button
                  key={id}
                  onClick={() => handleCategorySelect(id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`relative p-4 rounded-2xl border text-left transition-all duration-300 ${
                    isSelected
                      ? 'border-[#7000FF] bg-[#7000FF]/20 shadow-[0_0_20px_rgba(112,0,255,0.3)]'
                      : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                  }`}
                >
                  {/* Icon */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${color}20`, border: `1px solid ${color}40` }}
                  >
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>

                  {/* Title */}
                  <p className="text-white font-semibold text-sm">{title}</p>

                  {/* Timing badge */}
                  <span
                    className="inline-block text-xs px-2 py-0.5 rounded-full font-mono mt-1"
                    style={{
                      backgroundColor: `${color}20`,
                      color,
                      border: `1px solid ${color}40`,
                    }}
                  >
                    {subtitle}
                  </span>

                  {/* Network / coin tags */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map(tag => (
                      <span
                        key={tag}
                        className="text-[10px] text-gray-400 bg-white/5 px-1.5 py-0.5 rounded font-mono"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Active dot */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#7000FF]" />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* ── Forms ──────────────────────────────────────────────────────── */}
          <AnimatePresence mode="wait">

            {/* ── Stablecoin / Crypto form ───────────────────────────────── */}
            {(selectedCategory === 'stablecoin' || selectedCategory === 'crypto') && (
              <motion.div
                key={selectedCategory}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
                className="glass-card p-8 rounded-2xl"
              >
                <form onSubmit={handleCryptoSend} className="space-y-6">

                  {/* Token select */}
                  <div>
                    <Label className="text-gray-300 mb-2 block">Select Token</Label>
                    <Select
                      value={cryptoForm.crypto}
                      onValueChange={(value) => {
                        const coins = SEND_COINS[selectedCategory];
                        const coin  = coins.find(c => c.id === value);
                        setCryptoForm(prev => ({
                          ...prev,
                          crypto: value,
                          network: coin?.networkFull || '',
                          to_address: '',
                        }));
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A051E] border-white/10">
                        {SEND_COINS[selectedCategory].map(coin => (
                          <SelectItem key={coin.id} value={coin.id} className="text-white hover:bg-white/10">
                            <div className="flex items-center gap-3">
                              <span>{CRYPTO_SYMBOLS[coin.id]} {coin.name}</span>
                              <span className="text-xs bg-white/10 px-2 py-0.5 rounded font-mono text-[#00F0FF]">
                                {coin.networkTag}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Available balance */}
                    {selectedBalance && (
                      <p className="text-gray-400 text-sm mt-2 font-mono">
                        Available: {selectedBalance.balance} {cryptoForm.crypto}
                      </p>
                    )}
                  </div>

                  {/* Network info badge */}
                  {selectedCoin && (
                    <div className="bg-[#7000FF]/10 border border-[#7000FF]/30 rounded-xl p-3 flex items-center gap-3">
                      <span className="text-xs bg-[#7000FF]/40 border border-[#7000FF]/60 text-[#00F0FF] px-3 py-1 rounded-full font-mono shrink-0">
                        {selectedCoin.networkTag}
                      </span>
                      <p className="text-gray-300 text-sm">{selectedCoin.networkFull}</p>
                    </div>
                  )}

                  {/* Recipient address */}
                  <div>
                    <Label className="text-gray-300 mb-2 block">Recipient Address</Label>
                    <Input
                      type="text"
                      placeholder={selectedCoin?.placeholder || 'Wallet address'}
                      value={cryptoForm.to_address}
                      onChange={(e) => setCryptoForm(prev => ({ ...prev, to_address: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono"
                      required
                    />
                    {selectedCoin && (
                      <p className="text-orange-400/80 text-xs mt-1.5">⚠️ {selectedCoin.warning}</p>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <Label className="text-gray-300 mb-2 block">Amount</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="any"
                        placeholder="0.00"
                        value={cryptoForm.amount}
                        onChange={(e) => setCryptoForm(prev => ({ ...prev, amount: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 pr-16 font-mono"
                        required
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-sm">
                        {cryptoForm.crypto}
                      </div>
                    </div>
                    {selectedBalance && cryptoForm.amount &&
                      parseFloat(cryptoForm.amount) > selectedBalance.balance && (
                      <p className="text-red-400 text-xs mt-1">Insufficient balance</p>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Network</span>
                      <span className="text-white font-mono">{selectedCoin?.networkFull}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Network Fee</span>
                      <span className="text-white font-mono">~ 0.0001 {cryptoForm.crypto}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Estimated Time</span>
                      <span className="text-white">
                        {selectedCategory === 'stablecoin' ? 'Instant' : '2-5 minutes'}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary"
                  >
                    {sending ? 'Sending...' : (
                      <span className="flex items-center justify-center gap-2">
                        Send {cryptoForm.crypto} <ArrowRight className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </form>
              </motion.div>
            )}

            {/* ── UAE Bank Transfer form ─────────────────────────────────── */}
            {selectedCategory === 'bank' && (
              <motion.div
                key="bank"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
              >
                {/* AED balance display */}
                <div className="glass-card p-5 rounded-2xl mb-4" data-testid="aed-balance-display">
                  <span className="text-gray-400 font-secondary text-sm uppercase tracking-wider">
                    Available AED Balance
                  </span>
                  <div className="text-3xl font-mono font-bold text-[#00F0FF] mt-1">
                    {formatCurrency(aedBalance)}
                  </div>
                </div>

                <div className="glass-card p-8 rounded-2xl">
                  <form onSubmit={handleBankSend} className="space-y-5">

                    {/* Amount */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Amount (AED)</Label>
                      <div className="relative">
                        <Input
                          type="number" step="0.01" min="10" max="100000" placeholder="0.00"
                          value={bankForm.amount}
                          onChange={(e) => handleBankInput('amount', e.target.value)}
                          className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono text-xl py-5 ${
                            bankErrors.amount ? 'border-red-500' : ''
                          }`}
                          required
                          data-testid="bank-transfer-amount-input"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-sm">
                          AED
                        </div>
                      </div>
                      {bankErrors.amount && (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{bankErrors.amount}
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">Min: 10 AED · Max: 100,000 AED</p>
                    </div>

                    {/* Bank select */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Bank Name</Label>
                      <Select
                        value={bankForm.bank_name}
                        onValueChange={(v) => setBankForm(prev => ({ ...prev, bank_name: v }))}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="bank-name-select">
                          <SelectValue placeholder="Select your bank" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A051E] border-white/10 max-h-60">
                          {uaeBanks.map(bank => (
                            <SelectItem key={bank} value={bank} className="text-white hover:bg-white/10">
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-gray-400" />{bank}
                              </div>
                            </SelectItem>
                          ))}
                          <SelectItem value="other" className="text-white hover:bg-white/10">Other Bank</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Account holder name */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Account Holder Name</Label>
                      <Input
                        type="text" placeholder="Full name as per bank account"
                        value={bankForm.account_holder_name}
                        onChange={(e) => handleBankInput('account_holder_name', e.target.value)}
                        className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 ${
                          bankErrors.account_holder_name ? 'border-red-500' : ''
                        }`}
                        required
                        data-testid="account-holder-name-input"
                      />
                      {bankErrors.account_holder_name && (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{bankErrors.account_holder_name}
                        </p>
                      )}
                    </div>

                    {/* Account number */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Account Number</Label>
                      <Input
                        type="text" placeholder="1234567890"
                        value={bankForm.account_number}
                        onChange={(e) => handleBankInput('account_number', e.target.value.replace(/[^0-9]/g, ''))}
                        className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono ${
                          bankErrors.account_number ? 'border-red-500' : ''
                        }`}
                        maxLength={16} required
                        data-testid="account-number-input"
                      />
                      {bankErrors.account_number ? (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{bankErrors.account_number}
                        </p>
                      ) : bankForm.account_number && !bankErrors.account_number && (
                        <p className="text-[#00FF94] text-xs mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />Valid account number format
                        </p>
                      )}
                    </div>

                    {/* IBAN */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">IBAN</Label>
                      <Input
                        type="text" placeholder="AE07 0331 2345 6789 0123 456"
                        value={formatIBAN(bankForm.iban)}
                        onChange={(e) => handleBankInput('iban', e.target.value)}
                        className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono tracking-wider ${
                          bankErrors.iban ? 'border-red-500' : ''
                        }`}
                        maxLength={28} required
                        data-testid="iban-input"
                      />
                      {bankErrors.iban ? (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{bankErrors.iban}
                        </p>
                      ) : bankForm.iban && !bankErrors.iban && validateIBAN(bankForm.iban).valid && (
                        <p className="text-[#00FF94] text-xs mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />Valid UAE IBAN format
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        Format: AE + 2 check digits + 3-digit bank code + 16-digit account
                      </p>
                    </div>

                    {/* SWIFT (optional) */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">
                        SWIFT/BIC Code <span className="text-gray-500 font-normal">(Optional)</span>
                      </Label>
                      <Input
                        type="text" placeholder="EMIRAEADXXX"
                        value={bankForm.swift_code}
                        onChange={(e) => setBankForm(prev => ({ ...prev, swift_code: e.target.value.toUpperCase() }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono"
                        maxLength={11}
                        data-testid="swift-code-input"
                      />
                      <p className="text-gray-500 text-xs mt-1">8 or 11 characters (optional for domestic transfers)</p>
                    </div>

                    {/* Summary */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Transfer Fee</span>
                        <span className="text-[#00FF94] font-mono">Free</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Processing Time</span>
                        <span className="text-white">1-2 business days</span>
                      </div>
                      {bankForm.amount && !bankErrors.amount && (
                        <div className="flex justify-between text-sm border-t border-white/10 pt-2">
                          <span className="text-gray-400">You will receive</span>
                          <span className="text-[#00F0FF] font-mono font-bold">
                            {formatCurrency(parseFloat(bankForm.amount) || 0)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Important notes */}
                    <div className="bg-[#7000FF]/10 border border-[#7000FF]/30 rounded-xl p-4 text-sm">
                      <p className="text-[#7000FF] font-semibold mb-1">Important Notes:</p>
                      <ul className="text-gray-400 space-y-1 text-xs">
                        <li>• Ensure bank details match exactly with your bank records</li>
                        <li>• Transfers to UAE banks only (IBAN starting with AE)</li>
                        <li>• Name mismatch may cause transfer rejection</li>
                      </ul>
                    </div>

                    <Button
                      type="submit"
                      disabled={bankSending || !isBankFormValid()}
                      className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="confirm-bank-transfer-btn"
                    >
                      {bankSending ? 'Processing...' : (
                        <span className="flex items-center justify-center gap-2">
                          Transfer to Bank <ArrowRight className="w-5 h-5" />
                        </span>
                      )}
                    </Button>
                  </form>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Send;
